import type { CallStatusIndicatorProps } from './index.types';
import {
  getCallStateColor,
  getCallStateLabel,
} from 'Modules/softphone/resolver';
import React from 'react';
import { cn } from '~/lib/utils';

/**
 * CallStatusIndicator
 *
 * Displays a status indicator with a colored dot and label text.
 * The color and label are automatically determined by the status prop.
 *
 * Uses the centralized resolver for consistent state representation.
 *
 * @param status - Call status that determines color and label
 * @param className - Additional CSS classes for the container
 * @param testID - Test ID for testing
 */
export const CallStatusIndicator = ({
  status,
  className,
  testID,
}: CallStatusIndicatorProps) => {
  const color = getCallStateColor(status);
  const label = getCallStateLabel(status);

  return (
    <div
      className={cn('flex items-center gap-2', className)}
      data-testid={testID}
    >
      <span
        className={cn('size-[5px] rounded-full bg-current', color)}
        data-testid={testID ? `${testID}-dot` : undefined}
        aria-hidden="true"
      />
      {label && (
        <span
          className="text-[14px] font-medium text-foreground"
          data-testid={testID ? `${testID}-label` : undefined}
        >
          {label}
        </span>
      )}
    </div>
  );
};
